import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { PaymentGateway } from '@interfaces/booking.interface';

export type PaymentFlow = 'tc' | 'default';

@Injectable({
  providedIn: 'root',
})
export class PaymentFlowService {
  private static readonly STORAGE_KEY = 'paymentFlow';
  private static readonly RETURN_URL_KEY = 'tcReturnUrl';
  private static readonly CURRENCY_KEY = 'paymentCurrency';
  /** Única moneda en la que se puede pagar con Mercado Pago (CAT-4825). */
  private static readonly MP_CURRENCY = 'ARS';
  /**
   * Moneda por pasarela (CAT-4903): resuelve `getGateway()` a partir de la
   * moneda de la reserva y viceversa en `markTurismoCityFlow(gateway)`.
   * Turismo City en una moneda que no está acá cae al motor de 1 paso (cash).
   */
  private static readonly GATEWAY_CURRENCY: Record<PaymentGateway, string> = {
    mercadopago: PaymentFlowService.MP_CURRENCY,
    wetravel: 'USD',
  };
  /**
   * Fallback de retorno a Turismo City cuando no hay referrer capturado.
   * Ajustar acá si TC define una URL de retorno propia.
   */
  private static readonly DEFAULT_RETURN_URL = 'https://www.turismocity.com.ar';
  /**
   * Host desde el que se sirven las reservas de Turismo City (CAT-5199).
   * Estar en este host es lo único que decide si se puede pagar con
   * pasarela, entre por /results o por el deep-link de /booking-payments.
   */
  private static readonly TC_HOST = 'welob2c.flaptek.com';

  /**
   * Criterio REAL de detección de Turismo City: la reserva viene de TC
   * cuando se cae a /booking-payments por deep-link con los parámetros
   * token + solutionID0 (+ journey*) en la URL, ej.:
   * /booking-payments?token=...&solutionID0=...&journey00=...&journey01=...
   *
   * El flujo interno del motor (results → booking-payments) navega SIN
   * query params (usa sessionStorage['booking']), por lo que resetea a
   * 'default' y no arrastra un 'tc' de una entrada anterior. Un refresh
   * en el flujo TC conserva los params en la URL y re-detecta 'tc'.
   */
  detectFromQueryParams(queryParams: Params): void {
    const isDeepLinkEntry = !!(
      queryParams['solutionID0'] && queryParams['token']
    );
    sessionStorage.setItem(
      PaymentFlowService.STORAGE_KEY,
      isDeepLinkEntry ? 'tc' : 'default'
    );
    if (isDeepLinkEntry) {
      // La página de TC desde la que vino el usuario, para poder devolverlo
      // ahí en los dead ends del flujo (timer vencido, error irrecuperable).
      const referrer = document.referrer;
      if (referrer && !referrer.startsWith(window.location.origin)) {
        sessionStorage.setItem(PaymentFlowService.RETURN_URL_KEY, referrer);
      }
    }
  }

  /**
   * ORIGEN de la reserva: el usuario vino de Turismo City. Gatea lo que
   * depende de ese origen (volver a TC en los dead ends, sin /results),
   * NO el flujo de pago — para eso está isMercadoPagoFlow().
   */
  isTurismoCityFlow(): boolean {
    return sessionStorage.getItem(PaymentFlowService.STORAGE_KEY) === 'tc';
  }

  /**
   * Moneda de la reserva, tomada de la respuesta del pricing
   * (priceBreakDown.currency): es la verdad del server y con la que se arma
   * la preferencia de MP. No se toma del query param `currency` del
   * deep-link porque es opcional y puede no coincidir.
   *
   * Se persiste en sessionStorage para que un refresh en el paso Pago pueda
   * restaurarlo ANTES de que el pricing vuelva a responder; el siguiente
   * pricing siempre la pisa.
   */
  setCurrency(currency: string | null | undefined): void {
    if (!currency) {
      sessionStorage.removeItem(PaymentFlowService.CURRENCY_KEY);
      return;
    }
    sessionStorage.setItem(PaymentFlowService.CURRENCY_KEY, currency);
  }

  /**
   * PASARELA de pago (CAT-4903): host de Turismo City (CAT-5199) + una
   * moneda con pasarela asignada (ARS→Mercado Pago, USD→WeTravel).
   * Cualquier otra moneda (u otro host) devuelve `null` y la pantalla se
   * comporta como el motor normal (1 paso, cash).
   */
  getGateway(): PaymentGateway | null {
    if (!this.isTurismoCityHost()) return null;
    const currency = sessionStorage
      .getItem(PaymentFlowService.CURRENCY_KEY)
      ?.toUpperCase();
    if (currency === PaymentFlowService.GATEWAY_CURRENCY.mercadopago) {
      return 'mercadopago';
    }
    if (currency === PaymentFlowService.GATEWAY_CURRENCY.wetravel) {
      return 'wetravel';
    }
    return null;
  }

  /**
   * FLUJO DE PAGO: paso Pago con selector de método, stepper de 4 pasos,
   * CONTINUAR en vez de RESERVAR. Existe para Turismo City en cualquier
   * moneda con pasarela asignada (ARS→Mercado Pago, USD→WeTravel, CAT-4903).
   */
  isGatewayFlow(): boolean {
    return this.getGateway() !== null;
  }

  /**
   * Wrapper de compatibilidad (CAT-4825 pre-CAT-4903): sigue existiendo para
   * no tocar a ningún caller — es exactamente `getGateway() === 'mercadopago'`.
   */
  isMercadoPagoFlow(): boolean {
    return this.getGateway() === 'mercadopago';
  }

  /**
   * Una vuelta de pasarela se marca como 'tc' + la moneda de esa pasarela
   * (CAT-4903). Cubre dos casos, ambos resueltos por el caller
   * (`confirmation.component.ts`, vía `resolveReturnGateway` sobre el
   * `paymentMethod` de sessionStorage): la sesión perdida (pestaña nueva,
   * sin `paymentMethod` guardado → cae a 'mercadopago') y la vuelta de
   * WeTravel en la misma pestaña, donde este método se llama en TODAS las
   * visitas a /confirmation/success|pending|failure|pendingEmission — sin
   * el argumento explícito, esa llamada pisaba siempre a ARS/Mercado Pago
   * sin importar la pasarela real de la reserva.
   */
  markTurismoCityFlow(gateway: PaymentGateway): void {
    sessionStorage.setItem(PaymentFlowService.STORAGE_KEY, 'tc');
    this.setCurrency(PaymentFlowService.GATEWAY_CURRENCY[gateway]);
  }

  private isTurismoCityHost(): boolean {
    return this.currentHostname().includes(PaymentFlowService.TC_HOST);
  }

  /** Aislado para poder simular el host en los tests. */
  private currentHostname(): string {
    return window.location.hostname;
  }

  /** URL para devolver al usuario a Turismo City. */
  getReturnUrl(): string {
    return (
      sessionStorage.getItem(PaymentFlowService.RETURN_URL_KEY) ??
      PaymentFlowService.DEFAULT_RETURN_URL
    );
  }
}
